/**
 * S-10 Deck (V8) — the center of the app, v5 "PIXEL SWEETHEART", Tinder-class.
 *
 * The table, rebuilt around three ideas:
 *
 *  1. FULL-BLEED PHOTO CARD — the photo IS the card (photos never pixelate,
 *     v5 §1): segmented progress bars up top (one per photo), tap left/right
 *     halves (or Space) to walk the gallery, a candy scrim keeps name/age/
 *     distance/presence legible. The card column is CENTERED with a hard
 *     max-width — phone gets full width, desktop gets a comfortable portrait
 *     card, never a left-jammed layout with a dead right half.
 *
 *  2. THE FULL PROFILE IS A SWIPE-UP — no "More" button: swipe up on the
 *     card (or the chevron, or ArrowUp) slides the whole dossier over the
 *     deck — every photo, bio, prompts, vitals, interests — a scrollable
 *     sheet. Swipe down / chevron / Esc closes. The action bar stays live
 *     underneath, so you can verdict straight from the dossier.
 *
 *  3. EVERY COMMIT IS A BEAT — drag-release AND button AND keyboard all run
 *     the same juicy commit: the deck advances instantly (optimistic; the
 *     card returns with an apology if the server refuses — ARCH §2.3 is
 *     untouched, every verdict is still re-validated at write time), a
 *     FLIGHT GHOST flies off with rotation + follow-through wearing its
 *     pixel sticker, a stage STAMP slams in, pixel particles burst, TICKER
 *     reacts (happy=like, heartbroken=reject, sweating=super-reject,
 *     cheering=crush/match), and the next card springs up from the stack.
 *     Super-reject gets its own dramatic-cute beat (bigger stamp, lavender
 *     flourish, skull confetti — no strobe, no gore). Matches still fire the
 *     full-screen <Reveal/> (pixel confetti / shatter-and-wink).
 *
 * Unchanged contracts: equal-weight LIKE/REJECT quota chips, crush ribbon +
 * #40 note, EC-22 cap card, SR through the same server resolver, S-12 reveal
 * fed by the swipe response, 3-card prefetch as render buffer only. Every
 * e2e hook is preserved: .deck-btn--like/--reject/--sr/--crush, .deck-btn
 * (rewind first), .deck-btn__count, .deck__card, .deck__hud .quota,
 * .face__name strong, .rvfx / .reveal__title. prefers-reduced-motion
 * collapses the theatrics to quick fades.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useDragControls } from "framer-motion";
import { formatCopy, resolveCopy } from "@humble/shared";
import {
  Button,
  Card,
  HijackedBadge,
  Mascot,
  PixelBolt,
  PixelCross,
  PixelHeart,
  PixelHeartCracked,
  PixelSkull,
  PixelSparkle,
  PixelStar,
  Reveal,
  Sheet,
  SwipeCard,
  prefersReducedMotion,
  springs,
  toast,
  VerifiedBadge,
  type MascotMood,
  type SwipeHandoff,
} from "../../design/index.js";
import { api, isApiError } from "../../lib/api.js";
import { sessionStore, useStore } from "../../lib/store.js";
import { useFeatureToggles } from "../settings/use-feature-toggles.js";
import {
  formatCountdown,
  useSwipeAction,
  type DeckCard,
  type Quotas,
  type SwipeResult,
} from "./use-swipe.js";
import "./deck.css";

type Verdict = "like" | "reject" | "crush" | "super_reject";

interface RevealState {
  result: SwipeResult;
  /** [me, them] — captured at swipe time for the avatar collision. */
  avatars?: [string, string];
}

/** The commit fly-off ghost — seeded from the live drag offset (or center). */
interface FlightState {
  key: number;
  verdict: Verdict;
  photo: string | null;
  name: string;
  x: number;
  y: number;
}

interface FxState {
  key: number;
  verdict: Verdict;
}

const STAMP_LABEL: Record<Verdict, () => string> = {
  like: () => resolveCopy("deck.swipe_labels.right") ?? "LIKE",
  reject: () => resolveCopy("deck.swipe_labels.left") ?? "REJECT",
  super_reject: () => resolveCopy("deck.swipe_labels.super") ?? "SUPER REJECT",
  crush: () => resolveCopy("deck.swipe_labels.crush") ?? "CRUSH",
};

/** Verdict → Ticker's instant take (a match upgrades it to cheer). */
const VERDICT_MOOD: Record<Verdict, MascotMood> = {
  like: "happy",
  reject: "heartbroken",
  super_reject: "error",
  crush: "cheer",
};

function cardPhotos(card: DeckCard | undefined): string[] {
  const p = card?.profile;
  if (p?.photo_urls?.length) return p.photo_urls;
  const avatar = p?.user?.avatar_url;
  return avatar ? [avatar] : [];
}

export function DeckScreen() {
  const [cards, setCards] = useState<DeckCard[]>([]);
  const [quotas, setQuotas] = useState<Quotas | null>(null);
  const [capResetAt, setCapResetAt] = useState<string | null>(null);
  const [blocked, setBlocked] = useState<string | null>(null); // 403 cooldown/restricted text
  const [reveal, setReveal] = useState<RevealState | null>(null);
  const [crushSheet, setCrushSheet] = useState<{ targetId: string; needsAck: boolean } | null>(null);
  const [crushNote, setCrushNote] = useState("");
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [photoIdx, setPhotoIdx] = useState(0);
  // commit theatrics — flight ghost, stage stamp/flourish, particle bursts
  const [flight, setFlight] = useState<FlightState | null>(null);
  const [fx, setFx] = useState<FxState | null>(null);
  const [bursts, setBursts] = useState<FxState[]>([]);
  const fxSeq = useRef(0);
  const fxTimers = useRef<number[]>([]);
  const fetching = useRef(false);
  const myAvatar = useStore(sessionStore, (s) => s.user?.avatar_url ?? null);
  const srEnabled = useFeatureToggles().super_reject_enabled;

  // Ticker peeks over the card and reacts to what you just did (v5 §2) —
  // pure cosmetics, resets to idle after a beat.
  const [peek, setPeek] = useState<MascotMood>("idle");
  const peekTimer = useRef<number | undefined>(undefined);
  const reactMascot = useCallback((mood: MascotMood, holdMs = 1600) => {
    setPeek(mood);
    window.clearTimeout(peekTimer.current);
    peekTimer.current = window.setTimeout(() => setPeek("idle"), holdMs);
  }, []);
  useEffect(
    () => () => {
      window.clearTimeout(peekTimer.current);
      for (const t of fxTimers.current) window.clearTimeout(t);
    },
    [],
  );

  const { send, busy } = useSwipeAction(setQuotas);

  const refill = useCallback(async (replace = false) => {
    if (fetching.current) return;
    fetching.current = true;
    try {
      const res = await api.get("/deck", { query: { limit: 3 } });
      setQuotas(res.quotas ?? null);
      setCards((prev) => {
        const base = replace ? [] : prev;
        const seen = new Set(base.map((c) => c.profile?.user?.id));
        return [...base, ...(res.cards ?? []).filter((c) => !seen.has(c.profile?.user?.id))];
      });
      setBlocked(null);
    } catch (err) {
      if (isApiError(err) && err.status === 403) setBlocked(err.message);
      else if (isApiError(err)) toast(err.message, "error");
    } finally {
      fetching.current = false;
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refill(true);
  }, [refill]);

  const top = cards[0];
  const topId = top?.profile?.user?.id;
  const photos = useMemo(() => cardPhotos(top), [top]);

  // fresh card → first photo
  useEffect(() => {
    setPhotoIdx(0);
  }, [topId]);

  const navPhoto = useCallback(
    (dir: 1 | -1) => {
      setPhotoIdx((i) => Math.min(Math.max(i + dir, 0), Math.max(photos.length - 1, 0)));
    },
    [photos.length],
  );

  /** The shared commit beat — ghost + stamp + particles + Ticker. */
  const launchFx = useCallback(
    (card: DeckCard, verdict: Verdict, handoff?: SwipeHandoff, photo?: string | null) => {
      const key = ++fxSeq.current;
      const reduced = prefersReducedMotion();
      if (!reduced) {
        setFlight({
          key,
          verdict,
          photo: photo ?? cardPhotos(card)[0] ?? null,
          name: card.profile?.user?.display_name ?? "",
          x: handoff?.x ?? 0,
          y: handoff?.y ?? 0,
        });
        setBursts((b) => [...b.slice(-2), { key, verdict }]);
        fxTimers.current.push(
          window.setTimeout(() => setBursts((b) => b.filter((x) => x.key !== key)), 1000),
        );
      }
      setFx({ key, verdict });
      fxTimers.current.push(
        window.setTimeout(() => setFx((s) => (s?.key === key ? null : s)), verdict === "super_reject" ? 900 : 650),
      );
      reactMascot(VERDICT_MOOD[verdict], verdict === "super_reject" ? 2000 : 1600);
    },
    [reactMascot],
  );

  /** Server refused — the card comes back (with the toast the hook raised). */
  const restoreTop = useCallback((card: DeckCard) => {
    setFlight(null);
    setCards((prev) =>
      prev[0]?.profile?.user?.id === card.profile?.user?.id ? prev : [card, ...prev],
    );
  }, []);

  async function resolveVerdict(
    verdict: Verdict,
    opts?: { note?: string; sendAnyway?: boolean; handoff?: SwipeHandoff },
  ) {
    if (!topId || busy) return;
    const swiped = top; // captured before the deck advances — feeds the reveal avatars
    const visiblePhoto = photos[photoIdx] ?? null;
    // Crush goes server-first (the #66b interstitial may bounce it back to the
    // note sheet); the swipe verbs advance optimistically for the Tinder feel.
    const optimistic = verdict !== "crush";
    if (optimistic) {
      launchFx(swiped, verdict, opts?.handoff, visiblePhoto);
      setExpanded(false);
      setCards((prev) => prev.slice(1));
    }
    const out = await send({
      targetId: topId,
      verdict,
      note: opts?.note,
      sendAnyway: opts?.sendAnyway,
    });
    if (out.kind === "cap") {
      setCapResetAt(out.resetAt ?? null); // EC-22: freeze, no verdict recorded
      return;
    }
    if (out.kind === "interstitial") {
      if (optimistic) restoreTop(swiped);
      setCrushSheet({ targetId: topId, needsAck: true }); // #66b send-anyway
      return;
    }
    if (out.kind === "error") {
      // card stays — server refused; put it back where it was
      if (optimistic) restoreTop(swiped);
      return;
    }
    if (!optimistic) {
      // crush accepted — now it earns the same juicy commit ("gone" cards
      // leave quietly: the hook already toasted, nothing to celebrate)
      if (out.kind === "resolved") launchFx(swiped, verdict, undefined, visiblePhoto);
      setExpanded(false);
      setCards((prev) => prev.slice(1));
    }
    const matched =
      out.kind === "resolved" && out.result && out.result.result.startsWith("matched_");
    if (matched) reactMascot("cheer", 2400);
    if (matched && out.kind === "resolved" && out.result) {
      const theirs =
        out.result.match?.counterpart?.avatar_url ??
        swiped?.profile?.photo_urls?.[0] ??
        swiped?.profile?.user?.avatar_url ??
        null;
      setReveal({
        result: out.result,
        avatars: myAvatar && theirs ? [myAvatar, theirs] : undefined,
      });
    }
    if (cards.length <= 3) void refill();
  }

  async function onRewind() {
    try {
      const res = await api.post("/swipes/rewind", { body: { client_event_id: crypto.randomUUID() } });
      toast(resolveCopy("deck.rewind_toast") ?? "Undone.", "success");
      if (res.quotas) setQuotas(res.quotas);
      await refill(true); // the rewound card re-enters near deck top server-side
    } catch (err) {
      if (isApiError(err)) toast(err.message, "error");
    }
  }

  async function fireCrush() {
    const sheet = crushSheet;
    if (!sheet) return;
    setCrushSheet(null);
    await resolveVerdict("crush", { note: crushNote.trim() || undefined, sendAnyway: sheet.needsAck });
    setCrushNote("");
  }

  // ---- Desktop keyboard shortcuts (PRD §10 — every gesture has an equal) --
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target as HTMLElement | null;
      if (
        t &&
        (t.tagName === "INPUT" ||
          t.tagName === "TEXTAREA" ||
          t.tagName === "SELECT" ||
          t.isContentEditable)
      )
        return;
      if (crushSheet) return; // the note sheet owns the keyboard
      if (reveal) {
        if (e.key === "Escape") setReveal(null);
        return;
      }
      switch (e.key) {
        case "ArrowLeft":
          e.preventDefault();
          if (top && !busy) void resolveVerdict("reject");
          break;
        case "ArrowRight":
          e.preventDefault();
          if (top && !busy) void resolveVerdict("like");
          break;
        case "ArrowUp":
          e.preventDefault();
          if (top) setExpanded(true);
          break;
        case "ArrowDown":
          e.preventDefault();
          setExpanded(false);
          break;
        case "Escape":
          setExpanded(false);
          break;
        case " ":
          if (t && t.tagName === "BUTTON") return; // Space still clicks buttons
          e.preventDefault();
          if (photos.length > 1) navPhoto(1);
          break;
        case "s":
        case "S":
          if (srEnabled && top && !busy) void resolveVerdict("super_reject");
          break;
        default:
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [top, topId, busy, crushSheet, reveal, srEnabled, photos.length, photoIdx, navPhoto, expanded]);

  if (blocked) {
    return (
      <div className="deck deck--center">
        <Card padding="lg" className="deck-scene">
          <Mascot mood="error" />
          <h1 className="type-display-3 deck-scene__title deck-scene__title--accent">Benched</h1>
          <p className="text-dim">{blocked}</p>
        </Card>
      </div>
    );
  }

  if (capResetAt !== null) {
    // EC-22 cap card: deck frozen until reset; server rejected idempotently.
    const capCopy = resolveCopy("deck.cap_reached") ?? "";
    return (
      <div className="deck deck--center">
        <Card padding="lg" className="deck-scene">
          <Mascot mood="sleepy" />
          <h1 className="type-display-3 deck-scene__title">Out of swipes</h1>
          <p className="text-dim">{formatCopy(capCopy, { "hh:mm": formatCountdown(capResetAt) })}</p>
          <Link to="/store" className="btn btn--primary" style={{ marginTop: 10 }}>
            Premium raises the ceiling
          </Link>
        </Card>
      </div>
    );
  }

  const nextPhoto =
    cards[1]?.profile?.photo_urls?.[0] ?? cards[1]?.profile?.user?.avatar_url ?? null;

  return (
    <div className="deck">
      <div className="deck__body">
        {/* HUD: likes AND rejects get equal billing (§2 — rejection is a first-class verb) */}
        <div className="deck__hud">
          <HudChip label={resolveCopy("deck.swipe_labels.right") ?? "LIKE"} left={quotas?.likes} tone="like" />
          <HudChip label={resolveCopy("deck.swipe_labels.left") ?? "REJECT"} left={quotas?.rejects} tone="reject" />
          {quotas && (
            <span className="deck__reset type-numeric text-faint">
              resets {formatCountdown(quotas.resets_at)}
            </span>
          )}
        </div>

        {top || flight ? (
          <div className="deck__stage">
            <div className="deck__stack">
              {/* Ticker peeks from behind the card and reacts to your verdicts */}
              <motion.div
                className="deck__peek"
                aria-hidden="true"
                animate={{ y: peek === "idle" ? 0 : -8, scale: peek === "idle" ? 1 : 1.12 }}
                transition={springs.jelly}
              >
                <Mascot mood={peek} size={32} />
              </motion.div>
              {cards[2] && <div className="deck__under deck__under--deep" aria-hidden="true" />}
              {cards[1] && (
                <motion.div
                  key={cards[1].profile?.user?.id ?? "under"}
                  className="deck__under deck__under--next"
                  aria-hidden="true"
                  initial={{ scale: 0.86, y: 30, opacity: 0 }}
                  animate={{ scale: 0.94, y: 16, opacity: 1 }}
                  transition={springs.weighty}
                >
                  {nextPhoto && <img src={nextPhoto} alt="" />}
                </motion.div>
              )}
              {top && (
                <SwipeCard
                  key={topId}
                  disabled={busy}
                  onVerdict={(v, handoff) => void resolveVerdict(v, { handoff })}
                  onSwipeUp={() => setExpanded(true)}
                  className="deck__card"
                >
                  <CardFace
                    card={top}
                    photos={photos}
                    photoIndex={photoIdx}
                    onPhotoNav={navPhoto}
                    onOpenProfile={() => setExpanded(true)}
                  />
                </SwipeCard>
              )}
              {/* the commit beat — fly-off ghost, stage stamp, pixel bursts */}
              <AnimatePresence>
                {flight && (
                  <VerdictFlight
                    key={flight.key}
                    flight={flight}
                    onDone={() => setFlight((f) => (f?.key === flight.key ? null : f))}
                  />
                )}
              </AnimatePresence>
              {bursts.map((b) => (
                <PixelBurst key={b.key} verdict={b.verdict} />
              ))}
              <AnimatePresence>
                {fx && fx.verdict === "super_reject" && (
                  <motion.div
                    key={`flash-${fx.key}`}
                    className="deck__srflash"
                    aria-hidden="true"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: [0, 0.4, 0] }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.55, ease: "easeOut" }}
                  />
                )}
              </AnimatePresence>
              <AnimatePresence>
                {fx && (
                  <motion.div
                    key={`stamp-${fx.key}`}
                    className={`deck__stamp deck__stamp--${fx.verdict}`}
                    aria-hidden="true"
                    initial={{ opacity: 0, scale: 1.7, rotate: fx.verdict === "reject" ? 9 : -9 }}
                    animate={{ opacity: 1, scale: 1, rotate: fx.verdict === "reject" ? 6 : -6 }}
                    exit={{ opacity: 0, scale: 0.9, transition: { duration: 0.14 } }}
                    transition={springs.slam}
                  >
                    {STAMP_LABEL[fx.verdict]()}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        ) : loading ? (
          <div className="deck__stage">
            <div className="deck__stack">
              <div className="deck__card deck__card--skeleton">
                <Mascot mood="loading" quip={resolveCopy("deck.loading_quip") ?? ""} />
              </div>
            </div>
          </div>
        ) : (
          <EmptyDeck />
        )}

        {/* swipe-up dossier — the full profile sliding over the deck; the
            action bar below stays live so you can verdict from in here */}
        <AnimatePresence>
          {expanded && top && (
            <ProfileSheet
              card={top}
              photos={photos}
              photoIndex={photoIdx}
              onPhotoNav={navPhoto}
              onClose={() => setExpanded(false)}
            />
          )}
        </AnimatePresence>
      </div>

      {/* Action bar — every gesture has a button equivalent (PRD §10); the
          soft coins fire the same commit beat as the swipe */}
      <div className="deck__actions" role="toolbar" aria-label="Swipe actions">
        <DeckBtn className="deck-btn deck-btn--rewind" title="Rewind" onClick={() => void onRewind()} disabled={busy}>
          <IconRewind />
        </DeckBtn>
        <DeckBtn
          className="deck-btn deck-btn--reject"
          title={resolveCopy("deck.swipe_labels.left") ?? "REJECT"}
          onClick={() => void resolveVerdict("reject")}
          disabled={!top || busy}
        >
          <PixelCross size={22} />
        </DeckBtn>
        {/* S-27 game toggles: the skull hides when the user opted out (server
            refuses regardless — SR_DISABLED); defaults ON so the selector is
            always present for the e2e. */}
        {srEnabled && (
          <DeckBtn
            className="deck-btn deck-btn--sr"
            title={formatCopy(resolveCopy("deck.sr_button_tooltip") ?? "", { n: quotas?.super_rejects ?? 0 })}
            onClick={() => void resolveVerdict("super_reject")}
            disabled={!top || busy}
          >
            <PixelSkull size={20} />
            <span className="deck-btn__count">{quotas?.super_rejects ?? "–"}</span>
          </DeckBtn>
        )}
        <DeckBtn
          className="deck-btn deck-btn--crush"
          title={formatCopy(resolveCopy("deck.crush_button_tooltip") ?? "", { n: quotas?.crushes ?? 0 })}
          onClick={() => topId && setCrushSheet({ targetId: topId, needsAck: false })}
          disabled={!top || busy}
        >
          <PixelStar size={20} />
          <span className="deck-btn__count">{quotas?.crushes ?? "–"}</span>
        </DeckBtn>
        <DeckBtn
          className="deck-btn deck-btn--like"
          title={resolveCopy("deck.swipe_labels.right") ?? "LIKE"}
          onClick={() => void resolveVerdict("like")}
          disabled={!top || busy}
        >
          <PixelHeart size={24} />
        </DeckBtn>
      </div>

      {/* desktop-only keyboard hints (hidden on touch — CSS gates it) */}
      <div className="deck__keys" aria-hidden="true">
        <span className="deck__key"><kbd>&larr;</kbd> {resolveCopy("deck.swipe_labels.left") ?? "REJECT"}</span>
        {srEnabled && (
          <span className="deck__key"><kbd>S</kbd> {resolveCopy("deck.swipe_labels.super") ?? "SUPER REJECT"}</span>
        )}
        <span className="deck__key"><kbd>space</kbd> photo</span>
        <span className="deck__key"><kbd>&uarr;</kbd> profile</span>
        <span className="deck__key"><kbd>&rarr;</kbd> {resolveCopy("deck.swipe_labels.right") ?? "LIKE"}</span>
      </div>

      {/* S-12 match reveal — the cinematic BEAT (both variants; copy keys
          arrive from the server, the client only resolves) */}
      <DeckReveal reveal={reveal} onClose={() => setReveal(null)} />

      {/* Crush sheet (#39/#40 note ≤140 + #66b interstitial) */}
      <Sheet
        open={crushSheet !== null}
        onClose={() => {
          setCrushSheet(null);
          setCrushNote("");
        }}
        title="Crush"
      >
        {crushSheet?.needsAck && (
          <p className="text-dim" style={{ marginTop: 0 }}>
            {resolveCopy("chat.send_anyway")}
          </p>
        )}
        <div className="field">
          <label htmlFor="crush-note">Say why (optional, 140 max)</label>
          <textarea
            id="crush-note"
            maxLength={140}
            rows={3}
            value={crushNote}
            onChange={(e) => setCrushNote(e.target.value)}
          />
        </div>
        <Button variant="primary" block onClick={() => void fireCrush()} loading={busy}>
          {crushSheet?.needsAck ? "Send anyway" : "Send the Crush"}
        </Button>
      </Sheet>
    </div>
  );
}

/** Squishy candy coin — squash press, jelly hover, tint per verb
 *  (classes preserved). */
function DeckBtn({
  className,
  title,
  onClick,
  disabled,
  children,
}: {
  className: string;
  title: string;
  onClick: () => void;
  disabled?: boolean;
  children: ReactNode;
}) {
  return (
    <motion.button
      type="button"
      className={className}
      title={title}
      onClick={onClick}
      disabled={disabled}
      whileTap={disabled ? undefined : { scale: 0.82 }}
      whileHover={disabled ? undefined : { scale: 1.1, y: -3, rotate: -3 }}
      transition={springs.squish}
    >
      {children}
    </motion.button>
  );
}

function HudChip({ label, left, tone }: { label: string; left: number | undefined; tone: "like" | "reject" }) {
  const out = left === 0;
  return (
    <span
      className={`quota deck__chip deck__chip--${tone}${out ? " quota--out" : ""}`}
      aria-label={`${label}: ${left ?? "–"} left today`}
    >
      <span className="quota__label">{label}</span>
      <span className="quota__count deck__chip-count">
        <AnimatePresence mode="popLayout" initial={false}>
          <motion.span
            key={left ?? "none"}
            initial={{ scale: 1.5, opacity: 0, y: -6 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.6, opacity: 0, y: 6 }}
            transition={springs.bouncy}
            style={{ display: "inline-block" }}
          >
            {left ?? "–"}
          </motion.span>
        </AnimatePresence>
      </span>
    </span>
  );
}

/* ------------------------------------------------------------ card face ---- */

function presenceLine(bucket: "online" | "today" | "this_week") {
  return resolveCopy(
    bucket === "online"
      ? "chat.presence_online"
      : bucket === "today"
        ? "chat.presence_today"
        : "chat.presence_week",
  );
}

/**
 * The full-bleed photo face (Tinder-style): the photo fills the card,
 * segmented progress bars walk the gallery (tap left/right halves), a candy
 * scrim keeps the meta legible. Also plated by S-14 Today's Menu (photos +
 * meta only there — no gallery controller, no open-profile chevron).
 */
export function CardFace({
  card,
  photos: photosProp,
  photoIndex,
  onPhotoNav,
  onOpenProfile,
}: {
  card: DeckCard;
  /** Controlled gallery (the deck drives it; the menu lets it self-manage). */
  photos?: string[];
  photoIndex?: number;
  onPhotoNav?: (dir: 1 | -1) => void;
  onOpenProfile?: () => void;
}) {
  const p = card.profile ?? {};
  const user = p.user ?? {};
  // Server-signed viewer-bound URLs (plain <img> renderable — no bearer needed).
  const photos = photosProp ?? cardPhotos(card);
  const [innerIdx, setInnerIdx] = useState(0);
  const idx = Math.min(photoIndex ?? innerIdx, Math.max(photos.length - 1, 0));
  const nav = useCallback(
    (dir: 1 | -1) => {
      if (onPhotoNav) onPhotoNav(dir);
      else setInnerIdx((i) => Math.min(Math.max(i + dir, 0), Math.max(photos.length - 1, 0)));
    },
    [onPhotoNav, photos.length],
  );
  const photo = photos[idx] ?? null;
  const [imgOk, setImgOk] = useState(true);
  useEffect(() => setImgOk(true), [photo]);
  const crush = card.crush;
  // SM-6 public overlay: server-sourced only (never computed here). The badge
  // is permanent while overlayed.
  const hijacked = p.overlayed === true;

  return (
    <div className="face face--bleed">
      {/* the photo bleeds past the card box so the drag parallax never
          reveals an edge; the chrome below anchors to the box itself */}
      <div className="face__photo">
        <AnimatePresence initial={false} mode="popLayout">
          {photo && imgOk ? (
            <motion.img
              key={photo}
              src={photo}
              alt=""
              onError={() => setImgOk(false)}
              initial={{ opacity: 0, scale: 1.04 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.18 }}
            />
          ) : (
            <div key="fallback" className="face__fallback" aria-hidden="true">
              {(user.display_name ?? "?").slice(0, 1).toUpperCase()}
            </div>
          )}
        </AnimatePresence>
      </div>
      <div className="face__scrim" aria-hidden="true" />
      {photos.length > 1 && (
        <div className="face__segs" aria-hidden="true">
          {photos.map((u, i) => (
            <span key={u} className={`face__seg${i === idx ? " face__seg--on" : ""}`} />
          ))}
        </div>
      )}
      {photos.length > 1 && (
        <>
          <button
            type="button"
            className="face__photonav face__photonav--prev"
            aria-label={`${resolveCopy("deck.photo_prev") ?? "Previous photo"} (${idx + 1}/${photos.length})`}
            onClick={() => nav(-1)}
          />
          <button
            type="button"
            className="face__photonav face__photonav--next"
            aria-label={`${resolveCopy("deck.photo_next") ?? "Next photo"} (${idx + 1}/${photos.length})`}
            onClick={() => nav(1)}
          />
        </>
      )}
      {crush?.ribbon && <div className="face__ribbon">{resolveCopy("deck.crush_ribbon")}</div>}
      <div className="face__meta">
        <div className="face__name">
          <strong>{user.display_name ?? "Someone"}</strong>
          {p.age !== undefined && <span className="face__age type-numeric">{p.age}</span>}
          {user.verified && <VerifiedBadge />}
        </div>
        {hijacked && (
          <div className="face__hijack">
            <HijackedBadge name={p.hijacked_by ?? "?"} />
          </div>
        )}
        <div className="face__facts">
          {p.presence_bucket && (
            // "someone's home" — server-sourced bucket only (#51: absent = nothing)
            <span className={`face__presence face__presence--${p.presence_bucket}`}>
              <span className="face__presence-dot" aria-hidden="true" />
              {presenceLine(p.presence_bucket)}
            </span>
          )}
          {p.distance_km !== undefined && (
            <span className="face__distance type-numeric">{p.distance_km} km away</span>
          )}
        </div>
        {crush && (crush.note !== undefined || crush.note_folded) && (
          <div className="face__crushnote type-editorial">
            {crush.note_folded ? (
              <em className="text-faint">{resolveCopy("chat.hidden_by_filters")}</em>
            ) : (
              <em>“{crush.note}”</em>
            )}
          </div>
        )}
        {(p.interests ?? []).length > 0 && (
          <div className="face__chips">
            {(p.interests ?? []).slice(0, 3).map((i) => (
              <span key={i} className="badge">
                {i}
              </span>
            ))}
          </div>
        )}
        {card.why && card.why.length > 0 && (
          <div className="face__chips">
            {card.why.map((w) => (
              <span key={w} className="badge face__why">
                {w}
              </span>
            ))}
          </div>
        )}
      </div>
      {onOpenProfile && (
        <button
          type="button"
          className="face__expand"
          title={resolveCopy("deck.open_profile") ?? "Open the full profile"}
          aria-label={resolveCopy("deck.open_profile") ?? "Open the full profile"}
          onClick={onOpenProfile}
        >
          <IconChevronUp />
        </button>
      )}
    </div>
  );
}

/* -------------------------------------------------------- profile sheet ---- */

const VITAL_LABELS: Record<string, string> = {
  height_cm: "Height",
  job: "Job",
  education: "Education",
  drinking: "Drinking",
  smoking: "Smoking",
  kids: "Kids",
  religion: "Religion",
  politics: "Politics",
  zodiac: "Zodiac",
};

function vitalRows(vitals: Record<string, unknown> | undefined) {
  if (!vitals) return [];
  return Object.entries(vitals)
    .filter(([, v]) => v !== null && v !== undefined && String(v).trim() !== "")
    .map(([k, v]) => ({
      key: k,
      label: VITAL_LABELS[k] ?? k.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase()),
      value: k === "height_cm" ? `${String(v)} cm` : String(v),
    }));
}

/**
 * The dossier — the WHOLE profile as a sheet sliding over the deck (Tinder
 * "open profile"): gallery (same controller as the card), bio, prompts,
 * vitals, interests. Swipe down on the grip / chevron / Esc closes; the
 * action bar underneath stays live.
 */
function ProfileSheet({
  card,
  photos,
  photoIndex,
  onPhotoNav,
  onClose,
}: {
  card: DeckCard;
  photos: string[];
  photoIndex: number;
  onPhotoNav: (dir: 1 | -1) => void;
  onClose: () => void;
}) {
  const p = card.profile ?? {};
  const user = p.user ?? {};
  const crush = card.crush;
  const hijacked = p.overlayed === true;
  const bioOverlayed = hijacked && p.takeover?.overlay_bio !== undefined;
  const vitals = vitalRows(p.vitals as Record<string, unknown> | undefined);
  const closeRef = useRef<HTMLButtonElement>(null);
  const dragControls = useDragControls();

  useEffect(() => {
    closeRef.current?.focus({ preventScroll: true });
  }, []);

  return (
    <motion.section
      className="deck__profile"
      role="dialog"
      aria-label={`${user.display_name ?? "Profile"} — full profile`}
      initial={{ y: "106%" }}
      animate={{ y: 0 }}
      exit={{ y: "106%", transition: { duration: 0.2 } }}
      transition={springs.snappy}
      drag="y"
      dragListener={false}
      dragControls={dragControls}
      dragConstraints={{ top: 0, bottom: 0 }}
      dragElastic={{ top: 0, bottom: 0.6 }}
      onDragEnd={(_, info) => {
        if (info.offset.y > 110 || info.velocity.y > 800) onClose();
      }}
    >
      <div
        className="deck__profile-grip"
        onPointerDown={(e) => dragControls.start(e)}
      >
        <span className="deck__profile-handle" aria-hidden="true" />
        <button
          type="button"
          ref={closeRef}
          className="deck__profile-close"
          aria-label={resolveCopy("deck.close_profile") ?? "Close the full profile"}
          onClick={onClose}
        >
          <IconChevronDown />
        </button>
      </div>
      <div className="deck__profile-scroll">
        <div className="deck__profile-photo">
          <AnimatePresence initial={false} mode="popLayout">
            <motion.img
              key={photos[photoIndex] ?? "none"}
              src={photos[photoIndex]}
              alt=""
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.16 }}
            />
          </AnimatePresence>
          {photos.length > 1 && (
            <>
              <div className="face__segs" aria-hidden="true">
                {photos.map((u, i) => (
                  <span key={u} className={`face__seg${i === photoIndex ? " face__seg--on" : ""}`} />
                ))}
              </div>
              <button
                type="button"
                className="face__photonav face__photonav--prev"
                aria-label={resolveCopy("deck.photo_prev") ?? "Previous photo"}
                onClick={() => onPhotoNav(-1)}
              />
              <button
                type="button"
                className="face__photonav face__photonav--next"
                aria-label={resolveCopy("deck.photo_next") ?? "Next photo"}
                onClick={() => onPhotoNav(1)}
              />
            </>
          )}
        </div>

        <div className="deck__profile-head">
          <div className="face__name">
            <strong>{user.display_name ?? "Someone"}</strong>
            {p.age !== undefined && <span className="face__age type-numeric">{p.age}</span>}
            {user.verified && <VerifiedBadge />}
          </div>
          <div className="deck__profile-facts">
            {p.presence_bucket && (
              <span className={`face__presence face__presence--${p.presence_bucket}`}>
                <span className="face__presence-dot" aria-hidden="true" />
                {presenceLine(p.presence_bucket)}
              </span>
            )}
            {p.distance_km !== undefined && (
              <span className="text-dim type-numeric">{p.distance_km} km away</span>
            )}
            {p.pronouns && <span className="text-dim">{p.pronouns}</span>}
            {p.intent && <span className="badge">{p.intent}</span>}
          </div>
          {hijacked && (
            <div className="face__hijack">
              <HijackedBadge name={p.hijacked_by ?? "?"} />
            </div>
          )}
        </div>

        {crush && (crush.note !== undefined || crush.note_folded) && (
          <div className="face__crushnote type-editorial">
            {crush.note_folded ? (
              <em className="text-faint">{resolveCopy("chat.hidden_by_filters")}</em>
            ) : (
              <em>“{crush.note}”</em>
            )}
          </div>
        )}

        {hijacked && (
          <p className="face__overlay-marker text-faint">
            {resolveCopy("takeover.overlay_marker")}
          </p>
        )}

        {p.bio && (
          <section className="deck__profile-section">
            <h3 className="deck__profile-title">About</h3>
            <p className={bioOverlayed ? "face__overlayed" : undefined}>{p.bio}</p>
          </section>
        )}

        {p.anthem && (
          <section className="deck__profile-section">
            <h3 className="deck__profile-title">Anthem</h3>
            <p className="text-dim face__anthem type-editorial">♪ {p.anthem}</p>
          </section>
        )}

        {(p.prompts ?? []).length > 0 && (
          <section className="deck__profile-section">
            <h3 className="deck__profile-title">Prompts</h3>
            {(p.prompts ?? []).map((pr) => (
              <p key={pr.prompt_id} className={pr.overlayed ? "face__overlayed" : "deck__profile-prompt"}>
                {pr.answer}
              </p>
            ))}
          </section>
        )}

        {vitals.length > 0 && (
          <section className="deck__profile-section">
            <h3 className="deck__profile-title">Vitals</h3>
            <dl className="deck__profile-vitals">
              {vitals.map((v) => (
                <div key={v.key} className="deck__profile-vital">
                  <dt>{v.label}</dt>
                  <dd>{v.value}</dd>
                </div>
              ))}
            </dl>
          </section>
        )}

        {(p.interests ?? []).length > 0 && (
          <section className="deck__profile-section">
            <h3 className="deck__profile-title">Interests</h3>
            <div className="face__chips">
              {(p.interests ?? []).map((i) => (
                <span key={i} className="badge">
                  {i}
                </span>
              ))}
            </div>
          </section>
        )}

        {card.why && card.why.length > 0 && (
          <section className="deck__profile-section">
            <h3 className="deck__profile-title">Why this card</h3>
            <div className="face__chips">
              {card.why.map((w) => (
                <span key={w} className="badge face__why">
                  {w}
                </span>
              ))}
            </div>
          </section>
        )}

        <p className="deck__profile-hint text-faint">
          {resolveCopy("deck.profile_hint")}
        </p>
      </div>
    </motion.section>
  );
}

/* ------------------------------------------------------- commit theatrics -- */

/**
 * The fly-off ghost: a snapshot of the swiped card exiting with rotation +
 * follow-through and its pixel sticker slammed on. Super-reject sinks with
 * a dramatic beat instead. Purely decorative — never carries .deck__card.
 */
function VerdictFlight({ flight, onDone }: { flight: FlightState; onDone: () => void }) {
  const { verdict, photo, name, x, y } = flight;
  const isSr = verdict === "super_reject";
  const dir = verdict === "reject" ? -1 : 1;
  return (
    <motion.div
      className={`deck__flight deck__flight--${verdict}`}
      aria-hidden="true"
      initial={{ x, y, rotate: x / 20, opacity: 1 }}
      animate={
        isSr
          ? {
              y: [y, y - 16, 640],
              rotate: [x / 20, -3, 5],
              opacity: [1, 1, 0.4],
              transition: { duration: 0.66, times: [0, 0.4, 1], ease: "easeIn" },
            }
          : {
              x: dir * 640,
              y: y - 70,
              rotate: dir * 24,
              opacity: [1, 1, 0.5],
              transition: { duration: 0.48, ease: [0.3, 0.6, 0.45, 1] },
            }
      }
      exit={{ opacity: 0, transition: { duration: 0.1 } }}
      onAnimationComplete={onDone}
    >
      {photo ? (
        <img src={photo} alt="" />
      ) : (
        <div className="face__fallback">{(name || "?").slice(0, 1).toUpperCase()}</div>
      )}
      {/* SR's drama lives on the stage stamp — a second plate would double it */}
      {!isSr && (
        <motion.span
          className={`deck__flight-stamp deck__flight-stamp--${verdict}`}
          initial={{ opacity: 0, scale: 1.6, rotate: dir * -10 }}
          animate={{ opacity: 1, scale: 1, rotate: dir * -8 }}
          transition={springs.slam}
        >
          {STAMP_LABEL[verdict]()}
        </motion.span>
      )}
    </motion.div>
  );
}

/** Per-verdict pixel confetti — hearts for LIKE, cracked hearts for REJECT,
 *  skulls + bolts for the nuke, stars + hearts for CRUSH. Transform/opacity
 *  only; skipped entirely under prefers-reduced-motion (the caller gates). */
const BURST_COUNT = 12;

function PixelBurst({ verdict }: { verdict: Verdict }) {
  const pieces = useMemo(
    () =>
      Array.from({ length: BURST_COUNT }, (_, i) => {
        const angle = (i / BURST_COUNT) * Math.PI * 2 + Math.random() * 0.5;
        const dist = 70 + Math.random() * 110;
        return {
          x: Math.cos(angle) * dist,
          y: Math.sin(angle) * dist * 0.85 - 30,
          rotate: (Math.random() - 0.5) * 320,
          scale: 0.7 + Math.random() * 0.8,
          delay: Math.random() * 0.08,
          kind: i % 3,
        };
      }),
    [],
  );
  const glyph = (kind: number) => {
    switch (verdict) {
      case "like":
        return <PixelHeart size={14} />;
      case "reject":
        return kind === 0 ? <PixelSparkle size={12} /> : <PixelHeartCracked size={14} />;
      case "super_reject":
        return kind === 0 ? <PixelBolt size={13} /> : <PixelSkull size={14} />;
      case "crush":
        return kind === 0 ? <PixelHeart size={14} /> : <PixelStar size={14} />;
    }
  };
  return (
    <div className={`deck__burst deck__burst--${verdict}`} aria-hidden="true">
      {pieces.map((pc, i) => (
        <motion.span
          key={i}
          className="deck__burst-bit"
          initial={{ x: 0, y: 0, scale: 0.3, opacity: 0, rotate: 0 }}
          animate={{
            x: pc.x,
            y: [0, pc.y, pc.y + 46],
            scale: pc.scale,
            opacity: [0, 1, 1, 0],
            rotate: pc.rotate,
          }}
          transition={{ duration: 0.85, delay: pc.delay, ease: "easeOut" }}
        >
          {glyph(pc.kind)}
        </motion.span>
      ))}
    </div>
  );
}

/* ---------------------------------------------------------------- reveal --- */

/** S-12 — the full-screen warm reveal (classic irony / reject "oof, mutual"). */
function DeckReveal({ reveal, onClose }: { reveal: RevealState | null; onClose: () => void }) {
  const result = reveal?.result;
  const isReject = result?.result === "matched_reject";
  const title =
    (result?.reveal?.copy_key ? resolveCopy(result.reveal.copy_key) : undefined) ??
    resolveCopy("match.classic_title") ??
    "";
  const body = resolveCopy(isReject ? "match.reject_body" : "match.classic_body");
  const kicker = resolveCopy(isReject ? "match.reject_kicker" : "match.classic_kicker");
  const crushCtx = result?.reveal?.crush_context as
    | { comment?: string; element_snapshot?: unknown }
    | undefined;
  return (
    <Reveal
      open={reveal !== null}
      kind={isReject ? "reject" : "classic"}
      kicker={kicker}
      title={title}
      avatars={reveal?.avatars}
      onClose={onClose}
    >
      <p className="deck-reveal__body">{body}</p>
      {crushCtx?.comment && (
        <p className="face__crushnote type-editorial">
          <em>“{crushCtx.comment}”</em>
        </p>
      )}
      <div className="rvfx__actions">
        <Link to="/matches" className="btn btn--primary" style={{ flex: 1 }}>
          Say something
        </Link>
        <Button variant="ghost" style={{ flex: 1 }} onClick={onClose}>
          Keep swiping
        </Button>
      </div>
    </Reveal>
  );
}

function EmptyDeck() {
  return (
    <div className="deck--center">
      <Card padding="lg" className="deck-scene">
        <Mascot mood="sleepy" />
        <h1 className="type-display-3 deck-scene__title">
          {resolveCopy("deck.empty_title") ?? "DECK'S EMPTY."}
        </h1>
        <p className="text-dim deck-scene__quip">
          {formatCopy(resolveCopy("deck.empty") ?? "", { radius: "your" })}
        </p>
        <div className="deck__empty-ctas">
          <Link to="/settings/discovery" className="btn">
            Widen filters
          </Link>
          <Link to="/store" className="btn">
            Get a Boost
          </Link>
          <Link to="/menu" className="btn">
            Today&apos;s Menu
          </Link>
        </div>
      </Card>
    </div>
  );
}

/* --- the rewind stays a smooth glyph (it's a secondary control, and arrows
       read better round); the verb coins wear the pixel set ---------------- */

function IconRewind() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 12a9 9 0 1 0 3-6.7" />
      <path d="M3 4v5h5" />
    </svg>
  );
}

function IconChevronUp() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 15l7-7 7 7" />
    </svg>
  );
}

function IconChevronDown() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 9l7 7 7-7" />
    </svg>
  );
}
